import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Stack, Typography } from '@mui/material';
import { FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiRadioGroup from 'features/ui/base-components/pai-radio-group';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiHelpIcon from '../../../../ui/base-components/pai-help-icon';
import PaiMenuItem from '../../../../ui/base-components/pai-menu-item';
import PaiSelect from '../../../../ui/base-components/pai-select';
import { SelectorIds } from '../../campaign-builder.types';

/**
 * Cooldown choices offered for re-contacting someone already approached in another
 * campaign. `0` means "never again", which is the platform default and what every
 * campaign did before this setting existed.
 */
const RECONTACT_COOLDOWN_OPTIONS: Array<{ days: number; labelKey: string }> = [
  { days: 0, labelKey: 'recontactCooldownNever' },
  { days: 14, labelKey: 'recontactCooldown2Weeks' },
  { days: 28, labelKey: 'recontactCooldown4Weeks' },
  { days: 60, labelKey: 'recontactCooldown2Months' },
  { days: 90, labelKey: 'recontactCooldown3Months' },
  { days: 180, labelKey: 'recontactCooldown6Months' },
];

interface Props {
  status: FunnelStatuses | undefined;
  approveMessages: boolean;
  recontactCooldownDays: number | undefined;
  linkedinAccounts: LinkedinAccount[];
  selectedLinkedinAccount: string | null;

  handleMessageApprovalChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleRecontactCooldownChange(event: React.ChangeEvent<HTMLInputElement>): void;
  isLinkedinAccountMissing(): boolean;
  handleSelectedLinkedinUserChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const CampaignSettings: React.FC<Props> = ({
  status,
  approveMessages,
  recontactCooldownDays,
  linkedinAccounts,
  selectedLinkedinAccount,
  handleMessageApprovalChange,
  handleRecontactCooldownChange,
  isLinkedinAccountMissing,
  handleSelectedLinkedinUserChange,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const linkedinAccountMissing = isLinkedinAccountMissing();
  const canChangeLinkedinAccount = status === FunnelStatuses.CREATED || linkedinAccountMissing;

  return (
    <div className={classes.campaignSettings}>
      <section className={classes.section}>
        <Typography className={classes.blueTitle}>{t('campaignSettings')}</Typography>
        <Typography className={classes.title}>{t('thisIsHowItGoes')}</Typography>
        <Typography className={classes.greyText}>{t('defineOperationalSettings')}</Typography>
      </section>
      <section id={SelectorIds.SETTINGS_APPROVE_MESSAGES} className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {t('selectApproveMessages')}
        </Typography>
        <Typography className={classes.greyText} mb={'8px'}>
          {t('approveMessagesDescription')}
        </Typography>
        <Stack gap={1}>
          <PaiRadioGroup
            value={approveMessages}
            items={[
              { label: t('no'), value: false },
              { label: t('yes'), value: true },
            ]}
            row={true}
            onChange={handleMessageApprovalChange}
          />
          {approveMessages ? <Typography className={classes.highlightText}>{t('approveMessagesNote')}</Typography> : null}
        </Stack>
      </section>
      <section id={SelectorIds.SETTINGS_LINKEDIN_ACCOUNT} className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Stack direction={'row'} alignItems={'center'} gap={1} mb={'8px'}>
          <Typography className={classes.subTitle}>{t('selectLinkedinAccount')}</Typography>
          {canChangeLinkedinAccount ? (
            <PaiHelpIcon tooltipText={t('linkedinAccountInstruction')} />
          ) : (
            <PaiHelpIcon tooltipText={t('changeLinkedinAccountWarning')} variant={'warning'} />
          )}
        </Stack>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('selectLinkedinAccountDescription')}
        </Typography>
        <PaiSelect
          value={selectedLinkedinAccount || ''}
          onChange={handleSelectedLinkedinUserChange}
          disabled={!canChangeLinkedinAccount}
          error={linkedinAccountMissing}
          errorText={t('linkedinAccountMissingWarning')}
          style={{ maxWidth: '450px' }}
        >
          {linkedinAccounts.map((account) => (
            <PaiMenuItem key={account._id} value={account._id}>
              {account.userEmail}
            </PaiMenuItem>
          ))}
        </PaiSelect>
        <Typography className={classes.greyText} mt={'12px'}>
          {t('campaignAccountLimitsHint')}
        </Typography>
      </section>
      <section id={SelectorIds.SETTINGS_RECONTACT_COOLDOWN} className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {t('selectRecontactCooldown')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('recontactCooldownDescription')}
        </Typography>
        <PaiSelect
          value={String(recontactCooldownDays ?? 0)}
          onChange={handleRecontactCooldownChange}
          style={{ maxWidth: '450px' }}
        >
          {RECONTACT_COOLDOWN_OPTIONS.map((option) => (
            <PaiMenuItem key={option.days} value={String(option.days)}>
              {t(option.labelKey)}
            </PaiMenuItem>
          ))}
        </PaiSelect>
        <Typography className={classes.greyText} mt={'8px'}>
          {t('recontactCooldownNote')}
        </Typography>
      </section>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  campaignSettings: {
    padding: '0 24px',
  },
  section: {
    position: 'relative',
    padding: '24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  highlightText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '18px',
    left: '0px',
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    '&.active': {
      display: 'inline-block',
    },
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
}));

export default CampaignSettings;
